import React, { useMemo, useState } from 'react';
import { catalogItemsForTool } from '../../../utils/billToItemCatalog';
import { toolTypeLabel } from '../../../utils/billToFlowTools';
import {
  aggregateWeeklyItemHistory,
  SALES_HISTORY_OPTIONS,
} from '../../../utils/billToDailySales';
import { formatInt, toNumber } from '../../../utils/billToOrderCalc';
import {
  buildSkuPlanningRows,
  closingStockRowsFromWizard,
} from '../../../utils/billToSkuPlanning';
import SkuPlanningTable from './SkuPlanningTable';

function displayProjected(value, formatter) {
  const text = String(value ?? '').trim();
  if (text === '') return '—';
  const n = toNumber(value);
  if (!Number.isFinite(n)) return text;
  return formatter(n);
}

function Section({ title, hint, actions, children, padded = true }) {
  return (
    <section className="min-w-0 rounded-2xl border border-gray-200 bg-white">
      <div className="flex flex-col gap-3 border-b border-gray-100 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900">{title}</h4>
          {hint ? <p className="mt-0.5 text-xs text-gray-500">{hint}</p> : null}
        </div>
        {actions}
      </div>
      <div className={padded ? 'p-5' : ''}>{children}</div>
    </section>
  );
}

function DetailItem({ label, value }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-medium uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-1 break-words text-sm font-semibold text-gray-900">{value || '—'}</p>
    </div>
  );
}

function StepReviewConfirm({ wizardData }) {
  const params = wizardData.parameters || {};
  const selectedItems = catalogItemsForTool(wizardData);
  const [skuSearch, setSkuSearch] = useState('');
  const salesHistoryLabel =
    SALES_HISTORY_OPTIONS.find((opt) => String(opt.value) === String(params.salesHistoryWeeks))
      ?.label || params.salesHistoryWeeks || '—';

  const closingStockRows = useMemo(
    () => closingStockRowsFromWizard(wizardData),
    [wizardData]
  );

  const weekCount = Math.max(3, toNumber(params.salesHistoryWeeks, 3));
  const weeklyHistory = useMemo(
    () => aggregateWeeklyItemHistory(wizardData.dailySalesRows || [], weekCount),
    [wizardData.dailySalesRows, weekCount]
  );

  const skuRows = useMemo(
    () =>
      buildSkuPlanningRows({
        items: selectedItems,
        wizardData,
        weeklyHistory,
        weekCount,
        closingStockRows,
      }),
    [selectedItems, wizardData, weeklyHistory, weekCount, closingStockRows]
  );

  const visibleSkuRows = useMemo(() => {
    const q = skuSearch.trim().toLowerCase();
    if (!q) return skuRows;
    return skuRows.filter(({ item }) =>
      [item.itemName, item.itemCode, item.subCategory, item.category]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [skuRows, skuSearch]);

  return (
    <div className="min-w-0 space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Step 4: Review &amp; Confirm</h3>
        <p className="mt-1 text-sm text-gray-500">
          Check the tool setup and selected SKUs before creating the tool.
        </p>
      </div>

      <Section title="Tool Details" hint="From Configure Tool and Set Parameters">
        <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
          <DetailItem label="Tool Name" value={wizardData.toolName} />
          <DetailItem
            label="Tool Code"
            value={wizardData.toolCode || 'Auto-generated from tool name'}
          />
          <DetailItem label="Outlet" value={wizardData.outletName} />
          <DetailItem label="Date" value={wizardData.toolDate} />
          <DetailItem label="Sales History" value={salesHistoryLabel} />
          <DetailItem label="Tool Type" value={toolTypeLabel(wizardData)} />
          <DetailItem
            label="Projected Sales"
            value={displayProjected(params.projectedSales, formatInt)}
          />
          <DetailItem
            label="Projected GC"
            value={displayProjected(params.projectedGc, formatInt)}
          />
          <div className="sm:col-span-2 lg:col-span-3">
            <DetailItem label="Description" value={wizardData.toolDescription || '—'} />
          </div>
        </div>
      </Section>

      <Section
        padded={false}
        title={`SKU Planning (${selectedItems.length})`}
        hint="Same values as Set Parameters. Final Order by Outlet is the edited outlet qty."
        actions={
          selectedItems.length ? (
            <input
              type="text"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              placeholder="Search SKU NAME or Id"
              className="w-full rounded-lg border border-gray-200 bg-[#F7E5D5] px-3 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand sm:w-64"
            />
          ) : null
        }
      >
        {selectedItems.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500">No SKUs selected.</p>
        ) : visibleSkuRows.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-gray-500">No SKUs match your search.</p>
        ) : (
          <SkuPlanningTable
            rows={visibleSkuRows}
            wizardData={wizardData}
            maxHeightClass="max-h-[440px]"
          />
        )}
      </Section>
    </div>
  );
}

export default StepReviewConfirm;
